import React, { useState, useEffect } from "react";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import ResetEmail from "./ResetEmail";
import { toast } from "react-hot-toast"; // Make sure this is imported
// You likely DON'T need to import Toaster here, but in your main App/Layout
// import { Toaster } from 'react-hot-toast';
import { authService } from "@/services/api/auth.service";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";

const ManageAccount = () => {
  const [activeTab, setActiveTab] = useState("forgot");
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Logout handler
  const handleLogout = () => {
    logout();
    navigate("/Signin-Signup");
  };

  // Forgot Password Flow
  const [forgotData, setForgotData] = useState({
    email: "",
    otp: "",
    verificationToken: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [forgotError, setForgotError] = useState("");
  const [isForgotLoading, setIsForgotLoading] = useState(false);

  // Set Password Form
  const [setFormData, setSetFormData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const [setError, setSetError] = useState("");
  const [isSetting, setIsSetting] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle forgot password form changes
  const handleForgotChange = (e) => {
    const { name, value } = e.target;
    setForgotData((prev) => ({ ...prev, [name]: value }));
    if (forgotError) setForgotError("");
  };

  // Step 1: Request password reset
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsForgotLoading(true);
    setForgotError("");

    const endpoint = "/api/v1/auth/forgot-password";
    const requestData = { email: forgotData.email };

   
    

    try {
      const response = await authService.forgotPassword(requestData);
   
      if (response.status === "success") {
      
        toast.success(response.message || "OTP sent to your email"); // Success toast for step 1
        setStep(2);
      } else {
        throw new Error(response.message || "Failed to send OTP");
      }
    } catch (error) {
    
      const errorMsg = error.message || "Failed to send OTP";
      setForgotError(errorMsg);
      toast.error(errorMsg); // Error toast
    } finally {
      setIsForgotLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsForgotLoading(true);
    setForgotError("");

    const endpoint = "/api/v1/auth/verify-reset-otp";
    const requestData = {
      email: forgotData.email,
      otp: forgotData.otp,
    };

 
    try {
      const response = await authService.verifyPasswordResetOTP(requestData);
    
      

      if (response.status === "success" && response.data?.verificationToken) {
        setForgotData((prev) => ({
          ...prev,
          verificationToken: response.data.verificationToken,
        }));
   
        toast.success(response.message || "OTP verified successfully"); // Success toast for step 2
        setStep(3);
      } else {
        throw new Error(
          response.message || "Failed to verify OTP or retrieve token"
        );
      }
    } catch (error) {

      const errorMsg = error.message || "Failed to verify OTP";
      setForgotError(errorMsg);
      toast.error(errorMsg); // Error toast
    } finally {
      setIsForgotLoading(false);
    }
  };

  // Step 3: Reset password
  const handleForgotResetPassword = async (e) => {
    e.preventDefault();

    if (forgotData.newPassword !== forgotData.confirmNewPassword) {
      setForgotError("Passwords don't match");
      toast.error("Passwords don't match");
      return;
    }
    if (forgotData.newPassword.length < 8) {
      setForgotError("Password must be at least 8 characters");
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!forgotData.verificationToken) {
      setForgotError("Verification token is missing. Please verify OTP again.");
      toast.error("Verification token is missing. Please verify OTP again.");
      setStep(2); // Go back to OTP step if token missing
      return;
    }

    const endpoint = "/api/v1/auth/reset-password";
    const requestData = {
      email: forgotData.email,
      verificationToken: forgotData.verificationToken,
      newPassword: forgotData.newPassword,
    };


    setIsForgotLoading(true);
    setForgotError("");

    try {
      const response = await authService.resetPassword(requestData);
   

      if (response.status === "success") {
    
        // --- THIS IS THE CORRECT PLACE FOR THE SUCCESS TOAST ---
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.message || "Password reset successfully",
          confirmButtonColor: "#3085d6",
        }).then(() => {
          handleLogout();
        });

        // --- /THIS IS THE CORRECT PLACE FOR THE SUCCESS TOAST ---

        // Reset the form and go back to the start
        setForgotData({
          email: "",
          otp: "",
          verificationToken: "",
          newPassword: "",
          confirmNewPassword: "",
        });
        setStep(1);
      } else {
        throw new Error(response.message || "Failed to reset password");
      }
    } catch (error) {
  
      const errorMsg = error.message || "Failed to reset password";
      setForgotError(errorMsg);
      toast.error(errorMsg); // Error toast
    } finally {
      setIsForgotLoading(false);
    }
  };

  // Handle set password form changes
  const handleSetFormChange = (e) => {
    const { name, value } = e.target;
    setSetFormData((prev) => ({ ...prev, [name]: value }));
    if (setError) setSetError("");
  };
  const fetchUserEmail = () => {
    const user = authService.getCurrentUser();
    if (user && user.email) {
      return user.email;
    }
    return null;
  };
  useEffect(() => {
    const loadEmail = async () => {
      try {
        const email = await fetchUserEmail();
        setForgotData((prev) => ({ ...prev, email }));
      } catch (error) {
        console.error("Failed to load user email:", error);
      }
    };

    loadEmail();
  }, []);

  // Handle set password submission
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setSetError("");
    setIsSetting(true);

    if (!setFormData.newPassword || !setFormData.confirmNewPassword) {
      setSetError("Both fields are required");
      toast.error("Both fields are required");
      setIsSetting(false);
      return;
    }
    if (setFormData.newPassword !== setFormData.confirmNewPassword) {
      setSetError("Passwords do not match");
      toast.error("Passwords do not match");
      setIsSetting(false);
      return;
    }
    if (setFormData.newPassword.length < 8) {
      setSetError("Password must be at least 8 characters");
      toast.error("Password must be at least 8 characters");
      setIsSetting(false);
      return;
    }

    // Using the reset password endpoint since there's no separate setPassword method
    const endpoint = "/api/v1/auth/reset-password";
    const requestData = {
      email: forgotData.email, // Use email from forgotData state
      verificationToken: forgotData.verificationToken, // Use verification token from forgotData state
      newPassword: setFormData.newPassword,
    };

   
    

    try {
      // Using the existing resetPassword method from authService
      const response = await authService.resetPassword(requestData);

      

      if (response.status === "success") {
     
        
        // Use SweetAlert for consistency with reset password success
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.message || "Password set successfully",
          confirmButtonColor: "#3085d6",
        }).then(() => {
          handleLogout();
        });

        // Don't clear the form data after success to maintain state
      } else {
        throw new Error(response.message || "Failed to set password");
      }
    } catch (error) {
    
      const errorMsg = error.message || "Failed to set password";
      setSetError(errorMsg);
      toast.error(errorMsg); // Error toast
    } finally {
      setIsSetting(false);
    }
  };

  // --- JSX Rendering ---
  return (
    <div className="bg-white p-6  mt-20 rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold mb-6">Password Management</h2>

      {/* Tab Navigation */}
      <div className="flex border-b mb-6">
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === "forgot"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("forgot");
            // Don't reset the form state or clear data when switching tabs
            setStep(1); // Only reset to step 1 when switching to forgot tab
            setForgotError(""); // Clear any previous errors
          }}
        >
          Forgot Password
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === "set"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("set");
            // Don't clear set password form state when switching tabs
            setSetError(""); // Only clear any previous errors
          }}
        >
          Set Password
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === "email"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => {
            setActiveTab("email");
          }}
        >
          Reset Email
        </button>
      </div>

      {/* Forgot Password Flow */}
      {activeTab === "forgot" && (
        <div>
          <div className="flex items-center mb-4">
            {step > 1 && (
              <button
                onClick={() => setStep((prev) => prev - 1)} // Allow going back
                className="mr-2 p-1 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h3 className="text-lg font-medium">
              {step === 1
                ? "Forgot Password"
                : step === 2
                ? "Verify OTP"
                : "Reset Password"}
              {step > 1 && forgotData.email ? ` for ${forgotData.email}` : ""}
            </h3>
          </div>

          {/* Display Inline Error Message (Optional) */}
          {forgotError && (
            <div
              className="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded mb-4 text-sm"
              role="alert"
            >
              <p>{forgotError}</p>
            </div>
          )}

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleForgotPassword} noValidate>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address and we'll send you an OTP to reset your
                password.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="forgot-email"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Email Address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  name="email"
                  value={forgotData.email}
                  readOnly
                  disabled
                  className="bg-gray-100 cursor-not-allowed border-gray-300 text-gray-700 rounded w-full py-2 px-3"
                />
                {forgotError && !forgotData.email && (
                  <p
                    id="email-error"
                    className="text-red-500 text-xs italic mt-1"
                  >
                    {forgotError}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={isForgotLoading || !forgotData.email}
                className={`w-full bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition duration-150 ease-in-out ${
                  isForgotLoading || !forgotData.email
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isForgotLoading ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
          )}

          {/* Step 2: Enter OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOtp} noValidate>
              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code sent to {forgotData.email}.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="otp-code"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  OTP Code
                </label>
                <input
                  id="otp-code"
                  type="text" // Use text to allow leading zeros if backend expects string
                  inputMode="numeric" // Hint for numeric keyboard on mobile
                  name="otp"
                  value={forgotData.otp}
                  onChange={handleForgotChange}
                  className={`border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    forgotError &&
                    (!forgotData.otp || forgotData.otp.length !== 6)
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  aria-describedby={
                    forgotError &&
                    (!forgotData.otp || forgotData.otp.length !== 6)
                      ? "otp-error"
                      : undefined
                  }
                />
                {forgotError &&
                  (!forgotData.otp || forgotData.otp.length !== 6) && (
                    <p
                      id="otp-error"
                      className="text-red-500 text-xs italic mt-1"
                    >
                      {forgotError}
                    </p>
                  )}
              </div>
              <button
                type="submit"
                disabled={isForgotLoading || forgotData.otp.length !== 6}
                className={`w-full bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition duration-150 ease-in-out ${
                  isForgotLoading || forgotData.otp.length !== 6
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isForgotLoading ? "Verifying..." : "Verify OTP"}
              </button>
              <button
                type="button"
                onClick={() => handleForgotPassword(new Event("submit"))} // Resend OTP logic might need adjustment based on API
                disabled={isForgotLoading}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Didn't receive code? Resend OTP
              </button>
            </form>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <form onSubmit={handleForgotResetPassword} noValidate>
              <p className="text-sm text-gray-600 mb-4">
                Create a new strong password for your account.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="forgot-new-password"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="forgot-new-password"
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={forgotData.newPassword}
                    onChange={handleForgotChange}
                    className={`border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                      forgotError.includes("Password") &&
                      !forgotData.newPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    minLength={8}
                    aria-describedby={
                      forgotError.includes("Password") &&
                      !forgotData.newPassword
                        ? "new-password-error"
                        : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {forgotError.includes("Password") &&
                  !forgotData.newPassword && (
                    <p
                      id="new-password-error"
                      className="text-red-500 text-xs italic mt-1"
                    >
                      {forgotError}
                    </p>
                  )}
              </div>
              <div className="mb-6">
                <label
                  htmlFor="forgot-confirm-password"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="forgot-confirm-password"
                    type={showPassword ? "text" : "password"}
                    name="confirmNewPassword"
                    value={forgotData.confirmNewPassword}
                    onChange={handleForgotChange}
                    className={`border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                      forgotError.includes("match") &&
                      forgotData.confirmNewPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Confirm new password"
                    required
                    minLength={8}
                    aria-describedby={
                      forgotError.includes("match") &&
                      forgotData.confirmNewPassword
                        ? "confirm-password-error"
                        : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {forgotError.includes("match") &&
                  forgotData.confirmNewPassword && (
                    <p
                      id="confirm-password-error"
                      className="text-red-500 text-xs italic mt-1"
                    >
                      {forgotError}
                    </p>
                  )}
              </div>
              <button
                type="submit"
                disabled={
                  isForgotLoading ||
                  !forgotData.newPassword ||
                  !forgotData.confirmNewPassword ||
                  forgotData.newPassword !== forgotData.confirmNewPassword ||
                  forgotData.newPassword.length < 8
                }
                className={`w-full bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition duration-150 ease-in-out ${
                  isForgotLoading ||
                  !forgotData.newPassword ||
                  !forgotData.confirmNewPassword ||
                  forgotData.newPassword !== forgotData.confirmNewPassword ||
                  forgotData.newPassword.length < 8
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isForgotLoading ? "Resetting Password..." : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Set Password Tab */}
      {activeTab === "set" && (
        <div>
          <div className="flex items-center mb-4">
            {step > 1 && (
              <button
                onClick={() => setStep((prev) => prev - 1)} // Allow going back
                className="mr-2 p-1 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h3 className="text-lg font-medium">
              {step === 1
                ? "Set Password"
                : step === 2
                ? "Verify OTP"
                : "Reset Password"}
              {step > 1 && forgotData.email ? ` for ${forgotData.email}` : ""}
            </h3>
          </div>

          {/* Display Inline Error Message (Optional) */}
          {forgotError && (
            <div
              className="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded mb-4 text-sm"
              role="alert"
            >
              <p>{forgotError}</p>
            </div>
          )}

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleForgotPassword} noValidate>
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address and we'll send you an OTP to reset your
                password.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="forgot-email"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Email Address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  name="email"
                  value={forgotData.email}
                  readOnly
                  disabled
                  className="bg-gray-100 cursor-not-allowed border-gray-300 text-gray-700 rounded w-full py-2 px-3"
                />
                {forgotError && !forgotData.email && (
                  <p
                    id="email-error"
                    className="text-red-500 text-xs italic mt-1"
                  >
                    {forgotError}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={isForgotLoading || !forgotData.email}
                className={`w-full bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition duration-150 ease-in-out ${
                  isForgotLoading || !forgotData.email
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isForgotLoading ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
          )}

          {/* Step 2: Enter OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOtp} noValidate>
              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code sent to {forgotData.email}.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="otp-code"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  OTP Code
                </label>
                <input
                  id="otp-code"
                  type="text" // Use text to allow leading zeros if backend expects string
                  inputMode="numeric" // Hint for numeric keyboard on mobile
                  name="otp"
                  value={forgotData.otp}
                  onChange={handleForgotChange}
                  className={`border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    forgotError &&
                    (!forgotData.otp || forgotData.otp.length !== 6)
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  aria-describedby={
                    forgotError &&
                    (!forgotData.otp || forgotData.otp.length !== 6)
                      ? "otp-error"
                      : undefined
                  }
                />
                {forgotError &&
                  (!forgotData.otp || forgotData.otp.length !== 6) && (
                    <p
                      id="otp-error"
                      className="text-red-500 text-xs italic mt-1"
                    >
                      {forgotError}
                    </p>
                  )}
              </div>
              <button
                type="submit"
                disabled={isForgotLoading || forgotData.otp.length !== 6}
                className={`w-full bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition duration-150 ease-in-out ${
                  isForgotLoading || forgotData.otp.length !== 6
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isForgotLoading ? "Verifying..." : "Verify OTP"}
              </button>
              <button
                type="button"
                onClick={() => handleForgotPassword(new Event("submit"))} // Resend OTP logic might need adjustment based on API
                disabled={isForgotLoading}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Didn't receive code? Resend OTP
              </button>
            </form>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <form onSubmit={handleForgotResetPassword} noValidate>
              <p className="text-sm text-gray-600 mb-4">
                Create a new strong password for your account.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="forgot-new-password"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="forgot-new-password"
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    value={forgotData.newPassword}
                    onChange={handleForgotChange}
                    className={`border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                      forgotError.includes("Password") &&
                      !forgotData.newPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    minLength={8}
                    aria-describedby={
                      forgotError.includes("Password") &&
                      !forgotData.newPassword
                        ? "new-password-error"
                        : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {forgotError.includes("Password") &&
                  !forgotData.newPassword && (
                    <p
                      id="new-password-error"
                      className="text-red-500 text-xs italic mt-1"
                    >
                      {forgotError}
                    </p>
                  )}
              </div>
              <div className="mb-6">
                <label
                  htmlFor="forgot-confirm-password"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    id="forgot-confirm-password"
                    type={showPassword ? "text" : "password"}
                    name="confirmNewPassword"
                    value={forgotData.confirmNewPassword}
                    onChange={handleForgotChange}
                    className={`border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${
                      forgotError.includes("match") &&
                      forgotData.confirmNewPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Confirm new password"
                    required
                    minLength={8}
                    aria-describedby={
                      forgotError.includes("match") &&
                      forgotData.confirmNewPassword
                        ? "confirm-password-error"
                        : undefined
                    }
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {forgotError.includes("match") &&
                  forgotData.confirmNewPassword && (
                    <p
                      id="confirm-password-error"
                      className="text-red-500 text-xs italic mt-1"
                    >
                      {forgotError}
                    </p>
                  )}
              </div>
              <button
                type="submit"
                disabled={
                  isForgotLoading ||
                  !forgotData.newPassword ||
                  !forgotData.confirmNewPassword ||
                  forgotData.newPassword !== forgotData.confirmNewPassword ||
                  forgotData.newPassword.length < 8
                }
                className={`w-full bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition duration-150 ease-in-out ${
                  isForgotLoading ||
                  !forgotData.newPassword ||
                  !forgotData.confirmNewPassword ||
                  forgotData.newPassword !== forgotData.confirmNewPassword ||
                  forgotData.newPassword.length < 8
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isForgotLoading ? "Resetting Password..." : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      )}
      {/* Reset Email Tab Content */}
      {activeTab === "email" && <ResetEmail />}
    </div>
  );
};

export default ManageAccount;
//work properly but not usin new email
